import React, {useCallback, useMemo} from 'react';
import {useTranslation} from 'next-i18next';
import {
	Button,
	FormControl,
	FormLabel,
	Heading,
	ListItem,
	Text,
	Textarea,
	UnorderedList,
	VStack,
	ListIcon,
} from '@tempo/core';
import {ArrowRightIcon, MinusIcon} from '@tempo/icons';
import TitleRow from '@components/PageElements/PageHeader/TitleRow';
import PageLayout from '@deezer/next/shared/layouts/PageLayout';
import FooterRow from '@components/PageElements/FooterRow';
import FormSectionContainer from '@components/PageElements/FormSectionContainer';
import ArtistDiscographyTable from '@deezer/next/features/ClaimAccess/components/ArtistDiscographyTable';
import type {Release} from '@deezer/next/services/api/Starmania/requests/album/types';
import {
	ReleaseType,
	AlbumStatus,
} from '@deezer/next/services/api/Starmania/requests/album/types';
import FormSectionTitle from '@components/PageElements/FormSectionTitle';
import FormContainer from '@components/PageElements/FormContainer';
import FormSection from '@components/PageElements/FormSection';
import PageLoader from '@components/PageLoader';

type Props = {
	releases: Release[];
	artistComment: string;
	selectedReleaseIds: number[];
	lockedReleaseIdSet: ReadonlySet<number>;
	onSelectedReleaseIdsChange: (id: number) => void;
	onAllSelectedReleaseIdsChange: (ids: number[]) => void;
	onArtistCommentChange: (comment: string) => void;
	onBack: () => void;
	onSubmit: () => void;
	isSubmitting: boolean;
	isLoading: boolean;
};

const ArtistDiscographyTakedownCard = ({
	releases,
	artistComment,
	selectedReleaseIds,
	lockedReleaseIdSet,
	onArtistCommentChange,
	onSelectedReleaseIdsChange,
	onAllSelectedReleaseIdsChange,
	onSubmit,
	isSubmitting,
	isLoading,
	onBack,
}: Props) => {
	const {t} = useTranslation('d4c');

	const takedownReleases = useMemo(
		() => releases.filter(r => r.status === AlbumStatus.TAKEDOWN),
		[releases],
	);

	const albums = useMemo(
		() =>
			takedownReleases.filter(r => r.type.toLowerCase() === ReleaseType.ALBUM),
		[takedownReleases],
	);
	const eps = useMemo(
		() => takedownReleases.filter(r => r.type.toLowerCase() === ReleaseType.EP),
		[takedownReleases],
	);
	const singles = useMemo(
		() =>
			takedownReleases.filter(r => r.type.toLowerCase() === ReleaseType.SINGLE),
		[takedownReleases],
	);

	const selectedAlbumsIds = useMemo(
		() => albums.filter(r => selectedReleaseIds.includes(r.id)).map(r => r.id),
		[albums, selectedReleaseIds],
	);
	const selectedEpsIds = useMemo(
		() => eps.filter(r => selectedReleaseIds.includes(r.id)).map(r => r.id),
		[eps, selectedReleaseIds],
	);
	const selectedSinglesIds = useMemo(
		() => singles.filter(r => selectedReleaseIds.includes(r.id)).map(r => r.id),
		[singles, selectedReleaseIds],
	);

	const handleCommentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
		onArtistCommentChange(e.target.value);
	};

	const handleSelect = useCallback(
		(id: number) => {
			if (lockedReleaseIdSet.has(id)) return;
			onSelectedReleaseIdsChange(id);
		},
		[lockedReleaseIdSet, onSelectedReleaseIdsChange],
	);

	const handleSelectAll = useCallback(
		(ids: number[]) => {
			onAllSelectedReleaseIdsChange(ids);
		},
		[onAllSelectedReleaseIdsChange],
	);

	if (isLoading) return <PageLoader />;

	return (
		<PageLayout
			data-testid="artist-discography-takedown-card"
			header={<TitleRow title={t('requestaccess_title_newrequest_d4c-web')} />}
			footer={
				<FooterRow
					rightElements={
						<>
							<Button
								data-testid="back-button"
								variant="outline"
								onClick={onBack}
								size="small"
								leftIcon={<ArrowRightIcon transform="scaleX(-1)" />} // TODO fix that
							>
								{t('generic_action_back_d4c-web')}
							</Button>
							<Button
								data-testid="submit-button"
								isLoading={isSubmitting}
								isDisabled={selectedReleaseIds.length === 0}
								size="small"
								onClick={onSubmit}
							>
								{t('generic_action_sendrequest_d4c-web')}
							</Button>
						</>
					}
				/>
			}
		>
			<FormContainer>
				<FormSection>
					<VStack align="left" spacing="spacing.l">
						<FormSectionTitle
							title={t('requestaccessreviewdisco_title_takedowndisco_d4c-web')}
							subtitle={t(
								'requestaccessreviewdisco_text_problemwithdisco_d4c-web',
							)}
							subLink="https://creatorsupport.deezer.com"
						/>

						<VStack align="left">
							<Text
								variant="body.default.regular"
								color="text.neutral.secondary.default"
							>
								{t(
									'requestaccessreviewdisco_text_removedpreviouslylinked_d4c-web',
								)}
							</Text>

							<Text
								variant="body.default.regular"
								color="text.neutral.secondary.default"
							>
								{t(
									'requestaccessreviewdisco_text_helpsaccurateartisthistory_d4c-web',
								)}
							</Text>
							<UnorderedList
								variant="body.default.regular"
								color="text.neutral.secondary.default"
							>
								<ListItem>
									<ListIcon as={MinusIcon} />
									{t(
										'requestaccessreviewdisco_text_allreleasespreselectedbydef_d4c-web',
									)}
								</ListItem>
								<ListItem>
									<ListIcon as={MinusIcon} />
									{t(
										'requestaccessreviewdisco_text_cannotunselectmatchingrelease_d4c-web',
									)}
								</ListItem>
								<ListItem>
									<ListIcon as={MinusIcon} />
									{t(
										'requestaccessreviewdisco_text_deselectifreleasesdontbelongtoyou_d4c-web',
									)}
								</ListItem>
							</UnorderedList>
							<Text
								variant="body.default.regular"
								color="text.neutral.secondary.default"
							>
								{t(
									'requestaccessreviewdisco_text_ourteamcleandiscoprovidecorrectpage_d4c-web',
								)}
							</Text>
						</VStack>

						<FormSectionContainer>
							<VStack align="left" spacing="spacing.2xl">
								<VStack spacing="spacing.2xl">
									{albums.length > 0 && (
										<ArtistDiscographyTable
											title={t('charts_title_albums_d4c-web')}
											releases={albums}
											selectedReleaseIds={selectedAlbumsIds}
											lockedReleaseIdSet={lockedReleaseIdSet}
											onSelectAll={handleSelectAll}
											onSelect={handleSelect}
										/>
									)}

									{eps.length > 0 && (
										<ArtistDiscographyTable
											title={t('requestaccessreviewdisco_title_eps_d4c-web')}
											releases={eps}
											selectedReleaseIds={selectedEpsIds}
											lockedReleaseIdSet={lockedReleaseIdSet}
											onSelectAll={handleSelectAll}
											onSelect={handleSelect}
										/>
									)}

									{singles.length > 0 && (
										<ArtistDiscographyTable
											title={t(
												'requestaccessreviewdisco_title_singles_d4c-web',
											)}
											releases={singles}
											selectedReleaseIds={selectedSinglesIds}
											lockedReleaseIdSet={lockedReleaseIdSet}
											onSelectAll={handleSelectAll}
											onSelect={handleSelect}
										/>
									)}
								</VStack>

								<VStack align="left">
									<Heading variant="title.section" as="h2">
										{t(
											'requestaccessreviewdisco_title_additionaldetails_d4c-web',
										)}
									</Heading>
									<FormControl>
										<FormLabel>
											{t(
												'requestaccessreviewdisco_text_providedetailsaboutmissingorwrongreleases_d4c-web',
											)}
										</FormLabel>
										<Textarea
											value={artistComment}
											onChange={handleCommentChange}
											name="comment"
											rows={6}
										/>
									</FormControl>
								</VStack>
							</VStack>
						</FormSectionContainer>
					</VStack>
				</FormSection>
			</FormContainer>
		</PageLayout>
	);
};

export default ArtistDiscographyTakedownCard;
